/**
 * 3 · Scarico per la produzione.
 *
 * Due cose che la schermata deve rendere evidenti:
 *
 * - **il semaforo**. Ogni riga porta rosso/giallo/verde secondo la priorità, e
 *   accanto alla pastiglia il nome del colore è **scritto**: il colore da solo
 *   non basta a chi non lo distingue, e in reparto succede.
 * - **gli ordini bloccati non compaiono fra quelli da chiamare** (regola 15).
 *   Stanno in un riquadro a parte che dice cosa manca, perché un ordine che
 *   sparisce senza spiegazione sembra un guasto.
 */

import { Notifiche } from '../components/notifications';
import { Sezione } from '../components/section';
import { Stato } from '../components/state';
import { Spazio, Testata, Voce } from '../components/header';
import { Avviso, useFlusso } from '../flow-api';

interface Cassetta {
  codice: string;
  ordine: string;
  cliente: string;
  sku: string;
  colore: string;
  capi: number;
  pers: string[];
  chiuse: string[];
  urgente?: boolean;
  consegna?: string;
  carrelloProposto?: string | null;
}
interface VistaScarico {
  daChiamare: Cassetta[];
  inBaia: Cassetta[];
  /** The office's trolleys in order, then any taken off that still carry crates. */
  carrelli: Record<string, Cassetta[]>;
  /** Crates per trolley, as the office set it. */
  posti: number;
  /** Trolleys shown only because they still carry crates: nothing new goes on them. */
  fuoriServizio: string[];
  bloccati: {
    ordine: string;
    cliente: string;
    daNegozio: { articolo: string; colore: string; capi: number; prelevata: boolean }[];
  }[];
}

/** Il semaforo: pastiglia più nome scritto. */
function Semaforo({
  urgente,
  consegna,
}: {
  // `| undefined` esplicito e non solo `?`: chi disegna passa il campo com'è —
  // `urgente={c.urgente}` — e quel campo può essere assente. Con i tipi
  // facoltativi presi alla lettera, «assente» e «passato vuoto» sono due cose
  // diverse, e qui vanno bene tutte e due.
  urgente?: boolean | undefined;
  consegna?: string | undefined;
}) {
  const [colore, nome] = urgente ? ['var(--over)', 'rosso · urgente'] : ['var(--ok)', 'verde'];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
      <span
        style={{
          width: 10,
          height: 10,
          borderRadius: 5,
          background: colore,
          display: 'inline-block',
        }}
      />
      <span style={{ color: 'var(--ink2)' }}>
        {nome}
        {consegna ? ` · consegna ${consegna}` : ''}
      </span>
    </span>
  );
}

export function Scarico() {
  const { dati, caricando, ultimo, inCorso, fai, chiudiUltimo } = useFlusso<VistaScarico>(
    'scarico',
    '/scarico',
  );
  if (caricando || !dati) return <Stato quale="caricando" dice="Preparo il piano…" />;

  const carrelli = Object.entries(dati.carrelli);
  const carichi = carrelli.filter(([, c]) => c.length > 0).length;

  return (
    <div className="shell">
      <Testata>
        <Voce k="Piano di domani" v="preparato ieri alle 16:00" />
        <Voce k="Da chiamare" v={dati.daChiamare.length} />
        <Spazio />
        <button
          className="btn btn--primario"
          disabled={inCorso || carichi === 0}
          onClick={fai('/scarico/parti')}
        >
          Porta in produzione
        </button>
        <Notifiche />
      </Testata>

      <div className="main">
        <section className="booth" style={{ background: 'var(--panel)' }}>
          <Sezione titolo="Chiamate · aspetta che arrivino, poi su un carrello">
            {dati.inBaia.length === 0 ? (
              <p className="booth__vuoto">
                Chiamale dalla lista. Il magazzino esce <b>per ordine di vendita</b>: chiamandone
                una escono tutte le cassette del suo ordine, e l’elevatore ci mette qualche secondo.
              </p>
            ) : (
              dati.inBaia.map((c) => (
                <article key={c.codice} className="riga riga--part" style={{ marginBottom: 10 }}>
                  <div className="riga__cliente">
                    {c.codice} · {c.cliente}
                  </div>
                  <div className="riga__sku">
                    {c.sku} {c.colore} · {c.capi} capi
                  </div>
                  <div className="riga__pers">
                    {c.pers.length === 0 ? 'neutro · a fine linea' : c.pers.join(' → ')}
                  </div>
                  <div className="mods" style={{ marginTop: 8 }}>
                    {carrelli
                      .filter(([k]) => !dati.fuoriServizio.includes(k))
                      .map(([k, cs]) => (
                        <button
                          key={k}
                          className="mod"
                          disabled={inCorso || cs.length >= dati.posti}
                          onClick={fai('/scarico/carrello', { cassetta: c.codice, carrello: k })}
                        >
                          {k}
                        </button>
                      ))}
                    <button
                      className="mod mod--quiet"
                      disabled={inCorso}
                      onClick={fai('/scarico/coda', { cassetta: c.codice })}
                    >
                      non è il momento
                    </button>
                  </div>
                </article>
              ))
            )}
          </Sezione>

          <Sezione titolo="I carrelli" distanziata>
            <div className="cassette">
              {carrelli.map(([k, cs]) => (
                <div key={k} className={`cassetta${cs.length ? ' cassetta--aperta' : ''}`}>
                  {/*
                    Il nome del carrello è un link, come il nome di una cassetta
                    al banco: due sigle dicono se c'è posto, non cosa c'è sopra,
                    e chi sta per spingere il carrello in produzione vuole la
                    seconda cosa. Scheda a parte, così la baia non si perde.

                    Anche da vuoto, a differenza del banco — lì il link esiste
                    solo a cassetta confermata, perché prima non c'è nessun
                    foglio da aprire. Qui la pagina c'è sempre e la risposta
                    «non c'è niente sopra K2» è una risposta buona; un nome che
                    a volte si preme e a volte no insegna a non premerlo.
                  */}
                  <a
                    className="cassetta__cod cassetta__cod--apre"
                    href={`/carrello/${k}`}
                    target="_blank"
                    rel="noreferrer"
                    title={`Apri il contenuto di ${k}`}
                  >
                    {k}
                  </a>
                  <span className="cassetta__n">
                    {cs.length}/{dati.posti} · {cs.map((c) => c.codice).join(' ') || 'vuoto'}
                    {dati.fuoriServizio.includes(k) && ' · fuori servizio'}
                  </span>
                </div>
              ))}
            </div>
            <p className="mod__hint" style={{ marginTop: 8 }}>
              Il bip è facoltativo e il carrello non rifiuta: reparti diversi si mescolano con un
              avviso. Resta un limite solo il posto — la cassetta numero {dati.posti + 1} non ci
              sta.
            </p>
          </Sezione>
        </section>

        <section className="righe">
          <Sezione titolo="Da chiamare in baia">
            {dati.daChiamare.map((c) => (
              <article key={c.codice} className={`riga riga--${c.urgente ? 'part' : 'att'}`}>
                <div className="riga__cliente">{c.cliente}</div>
                <div className="riga__sku">
                  {c.codice} · {c.sku} {c.colore} · {c.capi} capi
                </div>
                <Semaforo urgente={c.urgente} consegna={c.consegna} />
                <div className="mods" style={{ marginTop: 8 }}>
                  <button
                    className="mod"
                    disabled={inCorso}
                    onClick={fai('/scarico/chiama', { cassetta: c.codice })}
                  >
                    Chiama in baia
                  </button>
                  {c.carrelloProposto && (
                    <span className="mod__hint">proposto {c.carrelloProposto}</span>
                  )}
                </div>
              </article>
            ))}

            {dati.bloccati.length > 0 && (
              <Sezione titolo="Fermi: manca merce" distanziata>
                {dati.bloccati.map((o) => (
                  <article
                    key={o.ordine}
                    className="riga"
                    style={{ borderLeftColor: 'var(--over)' }}
                  >
                    <div className="riga__cliente">{o.cliente}</div>
                    <div className="riga__sku">ordine {o.ordine}</div>
                    <div className="riga__conto">
                      manca{' '}
                      {o.daNegozio
                        .filter((r) => !r.prelevata)
                        .map((r) => `${r.articolo} ${r.colore} ×${r.capi}`)
                        .join(', ')}
                    </div>
                    <p className="mod__hint">
                      Non entra in produzione, nemmeno per la parte già arrivata: le sue cassette
                      non vengono chiamate. Si sblocca dai prelievi.
                    </p>
                  </article>
                ))}
              </Sezione>
            )}
          </Sezione>
        </section>
      </div>

      <Avviso esito={ultimo} onChiudi={chiudiUltimo} />
    </div>
  );
}
